import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import {
    ArrowUpRight, BadgeIndianRupee, CalendarCheck2, Check, CheckCheck, Crown, FileText, Gem, HandCoins, Heart,
    MessageCircle, PackageOpen, QrCode, Receipt, TrendingUp, UserRoundSearch,
} from 'lucide-react';
import styles from './GrowthPillars.module.css';
import Button from '../Button/Button';
import logoWhite from '../../assets/hirsta-logo-white.png';

const ease = [0.22, 1, 0.36, 1];

/** Count up to a number once it is on screen */
function CountUp({ to, prefix = '', className }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.6 });
    const mv = useMotionValue(0);
    const [v, setV] = useState(0);
    useMotionValueEvent(mv, 'change', setV);
    useEffect(() => {
        if (!inView) return undefined;
        const c = animate(mv, to, { duration: 1.6, ease });
        return () => c.stop();
    }, [inView, to, mv]);
    return <span ref={ref} className={className}>{prefix}{Math.round(v).toLocaleString('en-IN')}</span>;
}

/* =============================== visuals (sample data) =============================== */
function RevenueVisual() {
    const W = 360; const H = 130;
    const before = [40, 42, 41, 44, 43, 45, 44, 46, 45, 47];
    const after = [40, 44, 47, 53, 57, 64, 70, 78, 86, 96];
    const path = (arr) => arr.map((v, i) => `${i ? 'L' : 'M'}${(i / (arr.length - 1)) * W},${H - (v / 100) * H}`).join(' ');
    const area = `${path(after)} L${W},${H} L0,${H} Z`;
    return (
        <div className={styles.vCard}>
            <div className={styles.vHead}>
                <span><small>Revenue this month</small><strong><CountUp to={684200} prefix="₹" /></strong></span>
                <em className={styles.up}><TrendingUp size={12} /> 21% more</em>
            </div>
            <svg viewBox={`0 0 ${W} ${H + 6}`} className={styles.compare} preserveAspectRatio="none" aria-hidden="true">
                <defs>
                    <linearGradient id="gpArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#7b66ff" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#7b66ff" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {[0.33, 0.66].map((g) => <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} className={styles.gridLine} />)}
                <motion.path d={area} fill="url(#gpArea)" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.9, duration: 0.8 }} />
                <motion.path d={path(before)} fill="none" stroke="#c6c6cc" strokeWidth="2" strokeDasharray="5 5"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, ease }} />
                <motion.path d={path(after)} fill="none" stroke="#673de6" strokeWidth="3" strokeLinecap="round"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.5, delay: 0.3, ease }} />
            </svg>
            <div className={styles.legend}>
                <span><i className={styles.lgBefore} /> Before Hirsta</span>
                <span><i className={styles.lgAfter} /> With Hirsta</span>
            </div>
            <div className={styles.chips}>
                <span><Crown size={13} /><em>Member upsells</em><b>+₹12,400</b></span>
                <span><HandCoins size={13} /><em>Prepaid in advance</em><b>+₹50,000</b></span>
                <span><PackageOpen size={13} /><em>Packages sold</em><b>+₹28,000</b></span>
            </div>
            <small className={styles.sample}>Sample salon</small>
        </div>
    );
}

function BrandVisual() {
    const touch = [[Receipt, 'Bill'], [FileText, 'PDF'], [MessageCircle, 'WhatsApp'], [QrCode, 'UPI page']];
    return (
        <div className={styles.brandGrid}>
            <div className={styles.phone}>
                <div className={styles.notch} />
                <div className={styles.chat}>
                    <div className={styles.bubble}>
                        <div className={styles.preview}>
                            <span className={styles.previewLogo}><img src={logoWhite} alt="" /></span>
                            <span><strong>Invoice, ₹1,080</strong><small>Glow Studio</small></span>
                        </div>
                        <p>Hi Priya, thank you for visiting Glow Studio! Your bill and this month's offer are ready.</p>
                        <span className={styles.ticks}>10:42 <CheckCheck size={12} /></span>
                    </div>
                </div>
            </div>
            <div className={styles.brandSide}>
                <div className={styles.logoCard}>
                    <span className={styles.logoMark}>GS</span>
                    <span><strong>Your logo, everywhere</strong><small>Set once, shown on every touch</small></span>
                </div>
                <ul className={styles.touch}>
                    {touch.map(([Icon, label], i) => (
                        <motion.li key={label} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.12, duration: 0.4 }}>
                            <Icon size={13} /> {label}
                            <motion.b initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 + i * 0.12, type: 'spring', stiffness: 500, damping: 18 }}><Check size={10} strokeWidth={3.5} /></motion.b>
                        </motion.li>
                    ))}
                </ul>
                <div className={styles.billPage}>
                    <div className={styles.billRow}><span>Hair Spa</span><b>₹1,200</b></div>
                    <div className={styles.billRow}><span>Member 10% off</span><b className={styles.good}>−₹120</b></div>
                    <div className={styles.pay}><QrCode size={13} /> Pay ₹1,080 by UPI</div>
                </div>
            </div>
        </div>
    );
}

function LoyaltyVisual() {
    const steps = [
        { icon: UserRoundSearch, title: 'Priya has not visited in 64 days', sub: 'Flagged by Hirsta', tone: 'muted' },
        { icon: MessageCircle, title: 'WhatsApp offer sent', sub: '"We miss you! 20% off this week"', tone: 'purple' },
        { icon: CalendarCheck2, title: 'Booked for Saturday, 4:30 PM', sub: 'Hair Spa with Riya', tone: 'green' },
    ];
    return (
        <div className={styles.vCard}>
            <div className={styles.storyWrap}>
                <span className={styles.storyRail}><motion.i initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, delay: 0.2, ease }} /></span>
                <ol className={styles.story}>
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.li key={s.title} initial={{ opacity: 0, x: 18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.25 + i * 0.35, ease }}>
                                <span className={`${styles.storyIcon} ${styles[s.tone]}`}><Icon size={15} /></span>
                                <span><strong>{s.title}</strong><small>{s.sub}</small></span>
                            </motion.li>
                        );
                    })}
                </ol>
            </div>
            <div className={styles.loyalFoot}>
                <span><Heart size={13} /> <CountUp to={38} /> clients came back this month</span>
                <b><CountUp to={41600} prefix="+₹" /></b>
            </div>
            <small className={styles.sample}>Sample salon</small>
        </div>
    );
}

/* =============================== pillars =============================== */
const PILLARS = [
    {
        id: 'revenue', icon: BadgeIndianRupee, kicker: 'Grow revenue', tone: 'toneRevenue',
        title: 'Turn every visit into a bigger bill',
        text: 'Hirsta puts the right offer in front of your staff at the counter and collects money before the service is even given.',
        how: [
            'Membership and package offers show up on every bill, so upselling becomes a habit',
            'Prepaid cards bring cash in advance and keep clients loyal to your salon',
            'Clear staff credit and incentives give your team a reason to recommend more',
        ],
        visual: <RevenueVisual />,
    },
    {
        id: 'brand', icon: Gem, kicker: 'Build your brand', tone: 'toneBrand',
        title: 'Look like a premium salon at every touch',
        text: 'From the bill to the WhatsApp message, every moment carries your name and logo, so clients remember you and recommend you.',
        how: [
            'Your logo on every bill, PDF and WhatsApp link preview',
            'A clean bill page where clients see their visit and pay by UPI',
            'The same professional experience in every branch',
        ],
        visual: <BrandVisual />,
    },
    {
        id: 'loyalty', icon: Heart, kicker: 'Bring clients back', tone: 'toneLoyalty',
        title: 'Clients who come back, again and again',
        text: 'Most salons lose clients quietly. Hirsta shows you who is slipping away and brings them back with one message.',
        how: [
            'See every client who has not visited in 60 days and send one offer on WhatsApp',
            'Reminders cut no-shows, so booked chairs turn into paid bills',
            'Packages lock in future visits for months',
        ],
        visual: <LoyaltyVisual />,
    },
];

const copyVariants = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 18, filter: 'blur(6px)' }, show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease } } };
const wordV = { hidden: { opacity: 0, y: '0.5em' }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } } };

function Pillar({ p, index, last }) {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const inView = useInView(ref, { once: false, amount: 0.35 });
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
    const parallax = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [50, -50]);
    const fillRaw = useTransform(scrollYProgress, [0.35, 0.85], [0, 1]);
    const fill = useSpring(fillRaw, { stiffness: 140, damping: 30 });
    const Icon = p.icon;

    return (
        <section ref={ref} id={p.id} className={`${styles.pillar} ${index % 2 ? styles.flip : ''}`} aria-labelledby={`${p.id}-title`}>
            {/* growth rail */}
            <div className={styles.railCol} aria-hidden="true">
                <span className={`${styles.node} ${inView ? styles.nodeOn : ''}`}>{String(index + 1).padStart(2, '0')}</span>
                {!last && <span className={styles.segment}><motion.i style={{ scaleY: fill }} /></span>}
            </div>

            <motion.div className={styles.copy} variants={copyVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.35 }}>
                <motion.span className={styles.kicker} variants={item}><Icon size={14} /> {p.kicker}</motion.span>
                <motion.h3 id={`${p.id}-title`} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}>
                    {p.title.split(' ').map((w, i) => <motion.span key={i} className={styles.word} variants={wordV}>{w}&nbsp;</motion.span>)}
                </motion.h3>
                <motion.p variants={item}>{p.text}</motion.p>
                <ul>
                    {p.how.map((h) => (
                        <motion.li key={h} variants={item}>
                            <motion.span className={styles.tick} variants={{ hidden: { scale: 0 }, show: { scale: 1, transition: { type: 'spring', stiffness: 520, damping: 18 } } }}>
                                <Check size={12} strokeWidth={3.2} />
                            </motion.span>
                            <span>{h}</span>
                        </motion.li>
                    ))}
                </ul>
                <motion.div variants={item}><Button to="/demo" variant="ghost" icon={ArrowUpRight}>Show me how</Button></motion.div>
            </motion.div>

            <motion.div
                className={`${styles.stage} ${styles[p.tone]}`}
                initial={{ clipPath: 'inset(14% 10% 14% 10% round 30px)', opacity: 0, scale: 0.96 }}
                whileInView={{ clipPath: 'inset(0% 0% 0% 0% round 30px)', opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease }}
            >
                <motion.div className={styles.stageInner} style={{ y: parallax }}>{p.visual}</motion.div>
            </motion.div>
        </section>
    );
}

export default function GrowthPillars() {
    return (
        <div className={styles.list}>
            {PILLARS.map((p, i) => <Pillar key={p.id} p={p} index={i} last={i === PILLARS.length - 1} />)}
        </div>
    );
}
